// File: src/pages/SalesPersonPincodes.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";
import swal from "sweetalert";

// ✅ Use your canonical import paths
import {
  _Get,
  _GetByUrlOnly,
  _PutArea,
} from "../import/ApiEndPoint/ApiCalling";
import {
  All_Users_Main,
  UpdateServiceArea,
  viewstatedata, // "/pincode/by-state"
  viewstates, // "/pincode/states"
} from "../import/ApiEndPoint/Api";

const injectOnce = () => {
  if (document.getElementById("salesp-inline-css")) return;
  const s = document.createElement("style");
  s.id = "salesp-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .p-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .p-table{ width:100%; border-collapse:collapse; min-width:1050px; }
    .p-table thead th{ background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb; padding:8px; text-align:left; white-space:nowrap; }
    .p-row{ border-bottom:1px solid #f1f5f9; }
    .p-cell{ padding:8px; font-size:13px; color:#0f172a; vertical-align:middle; }
    .p-table tbody tr:hover{ background:#f9fbff; }
    .pill-muted{ color:#6b7280; font-size:12px; }
    .p-link{ cursor:pointer; color:#111827; font-weight:800; }
    .p-check-cell{ text-align:center; }

    /* modal filters */
    .pin-fbar{
      border:1px dashed #cbd5e1; background:#fff; border-radius:14px;
      padding:10px 12px; margin-bottom:10px;
    }
    .pin-frow{ display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; }
    .pin-fi{ flex:1 1 220px; min-width:220px; }
    .pin-fi label{ font-size:12px; font-weight:800; color:#0f172a; letter-spacing:.02em; margin-bottom:4px; }
    .pin-fi .form-control{ border-radius:12px; }
    .pin-pill{
      display:inline-flex; gap:8px; align-items:center;
      padding:6px 10px; border-radius:999px;
      background:#f1f5f9; border:1px solid #e5e7eb;
      font-weight:800; font-size:12px; color:#0f172a;
    }

    /* hierarchy indents */
    .indent-1{ padding-left: 20px; }
    .indent-2{ padding-left: 42px; }
    .indent-3{ padding-left: 64px; }

    .btn-mini{
      height: 28px;
      padding: 0 10px;
      border-radius: 10px;
      font-weight: 800;
    }

    .tag{
      display:inline-flex; align-items:center; gap:6px;
      padding:4px 10px; border-radius:999px;
      border:1px solid #e5e7eb; background:#fff;
      font-weight:800; font-size:12px;
    }
  `;
  document.head.appendChild(s);
};

const uniqByPincode = (arr) => {
  const seen = new Set();
  const out = [];
  for (const x of arr || []) {
    const key = String(x?.pincode || "");
    if (!seen.has(key)) {
      seen.add(key);
      out.push(x);
    }
  }
  return out;
};

const pinKey = (r) =>
  [r?.state, r?.district, r?.city, r?.pincode]
    .map((v) =>
      String(v ?? "")
        .trim()
        .toLowerCase(),
    )
    .join("|");

const getUserDisplayName = (u) => {
  if (!u) return "—";
  const full = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return (
    full ||
    u.name ||
    u.fullName ||
    u.username ||
    u.UserName ||
    u.email ||
    "Unnamed"
  );
};

export default function SalesPersonPincodes() {
  const [loading, setLoading] = useState(true);

  // all sales people
  const [salesPersons, setSalesPersons] = useState([]);

  // Search for sales person list
  const [personSearch, setPersonSearch] = useState("");

  // expand controls (main table)
  const [openPersons, setOpenPersons] = useState(() => new Set()); // personId
  const [openStates, setOpenStates] = useState(() => new Set()); // `${personId}||${state}`
  const [openDistricts, setOpenDistricts] = useState(() => new Set()); // `${personId}||${state}||${district}`

  // service table filter (inside each person)
  const [serviceSearch, setServiceSearch] = useState("");

  // dirty state per person
  const [dirtyById, setDirtyById] = useState({}); // { [personId]: true/false }

  // selected person for modal actions
  const [modal, setModal] = useState(false);
  const [activePerson, setActivePerson] = useState(null);

  // modal loading + data
  const [pinLoading, setPinLoading] = useState(false);

  // States dropdown + Search textbox
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [statePinList, setStatePinList] = useState([]); // pins of selected state
  const [pinSearch, setPinSearch] = useState(""); // client-side search inside selected state
  const [selectedPins, setSelectedPins] = useState([]); // selected pin objects

  // expand controls inside modal (district accordion)
  const [openDistrictsModal, setOpenDistrictsModal] = useState(() => new Set());

  useEffect(() => injectOnce(), []);

  // Initial load of sales persons
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        const res = await _Get(All_Users_Main, user?.database);
        const filtered = (res?.User || []).filter(
          (u) =>
            u?.rolename?.roleName === "Sales Person" && u?.status === "Active",
        );

        // normalize service + keep inside each object
        const normalized = filtered.map((p) => ({
          ...p,
          service: uniqByPincode(Array.isArray(p?.service) ? p.service : []),
        }));

        setSalesPersons(normalized);
      } catch (err) {
        console.error("[SalesPersonPincodes] users error:", err);
        swal("Failed to load users");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // -------------------- modal helpers --------------------
  const viewPincodeByStateUrl = (state) =>
    `${viewstatedata}?state=${encodeURIComponent(state || "")}`;

  const fetchAllStates = async () => {
    try {
      const res = await _GetByUrlOnly(viewstates);
      const states =
        res?.states ||
        res?.data?.states ||
        res?.StateList ||
        res?.stateList ||
        [];

      const cleaned = Array.from(
        new Set(
          (states || []).map((s) => String(s || "").trim()).filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b));

      setStateOptions(cleaned);
    } catch (e) {
      console.error("[SalesPersonPincodes] fetchAllStates error:", e);
      setStateOptions([]);
    }
  };

  const fetchPinsForState = async (state) => {
    if (!state) {
      setStatePinList([]);
      return;
    }

    setPinLoading(true);
    try {
      const res = await _GetByUrlOnly(viewPincodeByStateUrl(state));

      let list = Array.isArray(res?.pinCodeList) ? res.pinCodeList : null;

      if (!list) {
        const pins =
          res?.pincodes ||
          res?.pincodesOnly ||
          res?.pinCodes ||
          res?.PinCodes ||
          [];
        list = Array.isArray(pins)
          ? pins.map((p) => ({ pincode: p, state }))
          : [];
      }

      const normalized = (list || []).map((x) => ({
        pincode: x?.pincode ?? x,
        city: x?.city || "",
        district: x?.district || "",
        state: x?.state || state,
      }));

      setStatePinList(normalized);
      setOpenDistrictsModal(new Set());
    } catch (err) {
      console.error("[SalesPersonPincodes] fetchPinsForState error:", err);
      swal("Failed to load pincodes for selected state");
      setStatePinList([]);
    } finally {
      setPinLoading(false);
    }
  };

  // selection helpers for MODAL (selectedPins)
  const isPinSelected = (pin) =>
    selectedPins.some((x) => pinKey(x) === pinKey(pin));

  const addPins = (pins) => {
    setSelectedPins((prev) => {
      const map = new Map();
      for (const p of prev) map.set(pinKey(p), p);
      for (const p of pins || []) {
        if (!p) continue;
        map.set(pinKey(p), p);
      }
      return Array.from(map.values());
    });
  };

  const removePins = (pins) => {
    const removeKeys = new Set((pins || []).map((p) => pinKey(p)));
    setSelectedPins((prev) => prev.filter((p) => !removeKeys.has(pinKey(p))));
  };

  const togglePin = (pin) => {
    if (!pin) return;
    if (isPinSelected(pin)) removePins([pin]);
    else addPins([pin]);
  };

  const togglePinGroup = (group) => {
    const pins = Array.isArray(group) ? group : [];
    if (!pins.length) return;
    const allSelected = pins.every((p) => isPinSelected(p));
    if (allSelected) removePins(pins);
    else addPins(pins);
  };

  const toggleDistrictModalRow = (districtName) => {
    const key = String(districtName || "-");
    setOpenDistrictsModal((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // when state changes -> load pins
  useEffect(() => {
    if (!modal) return;

    setSelectedPins([]);
    setPinSearch("");
    setOpenDistrictsModal(new Set());

    if (!selectedState) {
      setStatePinList([]);
      return;
    }
    fetchPinsForState(selectedState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, modal]);

  const modalPinsFiltered = useMemo(() => {
    let arr = statePinList || [];
    const q = (pinSearch || "").trim().toLowerCase();
    if (!q) return arr;

    return arr.filter((x) =>
      [x?.state, x?.district, x?.city, x?.pincode].some((v) =>
        String(v || "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [statePinList, pinSearch]);

  const groupedModal = useMemo(() => {
    const byDistrict = new Map();
    for (const r of modalPinsFiltered || []) {
      const district = r?.district || "-";
      if (!byDistrict.has(district)) byDistrict.set(district, []);
      byDistrict.get(district).push(r);
    }

    const dists = Array.from(byDistrict.entries()).sort((a, b) =>
      String(a[0] || "").localeCompare(String(b[0] || "")),
    );

    return dists.map(([district, items]) => ({
      district,
      items: items.slice().sort((a, b) => {
        const c = String(a.city || "").localeCompare(String(b.city || ""));
        return c !== 0
          ? c
          : String(a.pincode || "").localeCompare(String(b.pincode || ""));
      }),
    }));
  }, [modalPinsFiltered]);

  // -------------------- hierarchy (MAIN table) helpers --------------------
  const filteredSalesPersons = useMemo(() => {
    const q = personSearch.trim().toLowerCase();
    if (!q) return salesPersons || [];
    return (salesPersons || []).filter((sp) =>
      getUserDisplayName(sp).toLowerCase().includes(q),
    );
  }, [salesPersons, personSearch]);

  const serviceFiltered = (svc) => {
    const q = serviceSearch.trim().toLowerCase();
    if (!q) return svc || [];
    return (svc || []).filter((s) =>
      [s?.state, s?.district, s?.city, s?.pincode].some((v) =>
        String(v || "")
          .toLowerCase()
          .includes(q),
      ),
    );
  };

  const groupService = (svc) => {
    const byState = new Map();
    for (const r of svc || []) {
      const state = r?.state || "-";
      const district = r?.district || "-";
      if (!byState.has(state)) byState.set(state, new Map());
      const distMap = byState.get(state);
      if (!distMap.has(district)) distMap.set(district, []);
      distMap.get(district).push(r);
    }

    const states = Array.from(byState.entries()).sort((a, b) =>
      String(a[0] || "").localeCompare(String(b[0] || "")),
    );

    return states.map(([state, distMap]) => ({
      state,
      districts: Array.from(distMap.entries())
        .sort((a, b) => String(a[0] || "").localeCompare(String(b[0] || "")))
        .map(([district, items]) => ({
          district,
          items: items.slice().sort((a, b) => {
            const c = String(a.city || "").localeCompare(String(b.city || ""));
            return c !== 0
              ? c
              : String(a.pincode || "").localeCompare(String(b.pincode || ""));
          }),
        })),
    }));
  };

  const togglePersonRow = (personId) => {
    setOpenPersons((prev) => {
      const next = new Set(prev);
      next.has(personId) ? next.delete(personId) : next.add(personId);
      return next;
    });
  };

  const toggleStateRow = (personId, stateName) => {
    const key = `${personId}||${stateName}`;
    setOpenStates((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleDistrictRow = (personId, stateName, districtName) => {
    const key = `${personId}||${stateName}||${districtName}`;
    setOpenDistricts((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  /* ----------------- REMOVE HELPERS (STATE / DISTRICT / PINCODE) - PER PERSON ----------------- */
  const markDirty = (personId, val) => {
    setDirtyById((prev) => ({ ...(prev || {}), [personId]: !!val }));
  };

  const updatePersonService = (personId, updater) => {
    setSalesPersons((prev) =>
      (prev || []).map((p) => {
        if (p?._id !== personId) return p;
        const current = uniqByPincode(
          Array.isArray(p?.service) ? p.service : [],
        );
        const nextSvc = uniqByPincode(updater(current) || []);
        if (nextSvc.length !== current.length) markDirty(personId, true);
        return { ...p, service: nextSvc };
      }),
    );
  };

  const removeState = (personId, stateName) => {
    if (!personId || !stateName) return;
    updatePersonService(personId, (svc) =>
      (svc || []).filter(
        (s) =>
          String(s?.state || "-").toLowerCase() !==
          String(stateName).toLowerCase(),
      ),
    );
  };

  const removeDistrict = (personId, stateName, districtName) => {
    if (!personId || !stateName || !districtName) return;
    updatePersonService(personId, (svc) =>
      (svc || []).filter(
        (s) =>
          !(
            String(s?.state || "-").toLowerCase() ===
              String(stateName).toLowerCase() &&
            String(s?.district || "-").toLowerCase() ===
              String(districtName).toLowerCase()
          ),
      ),
    );
  };

  const removePincode = (personId, pincode) => {
    if (!personId || !pincode) return;
    updatePersonService(personId, (svc) =>
      (svc || []).filter(
        (s) => String(s?.pincode || "") !== String(pincode || ""),
      ),
    );
  };

  // Persist service list for a person
  const saveChanges = async (p) => {
    if (!p?._id) return swal("Invalid sales person");
    setLoading(true);
    try {
      const payload = {
        id: p._id,
        service: (p?.service || []).map((s) => ({
          pincode: s?.pincode,
          city: s?.city,
          district: s?.district,
          state: s?.state,
        })),
      };
      const res = await _PutArea(UpdateServiceArea, payload);
      if (res?.status) {
        swal("Service area updated");
        markDirty(p._id, false);
      } else {
        swal("Error updating service area");
      }
    } catch (err) {
      console.error(err);
      swal("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Open modal for a particular person (from row button)
  const openModalForPerson = async (p) => {
    if (!p?._id) return;

    setActivePerson(p);
    setModal(true);

    setSelectedPins([]);
    setPinSearch("");
    setSelectedState("");
    setStatePinList([]);
    setOpenDistrictsModal(new Set());

    await fetchAllStates();
  };

  // Add selected pins into activePerson service (Save Changes persists)
  const addSelectedPincodes = () => {
    if (!activePerson?._id) return;

    const serviceShape = selectedPins.map((pp) => ({
      pincode: pp?.pincode,
      city: pp?.city,
      district: pp?.district,
      state: pp?.state,
    }));

    updatePersonService(activePerson._id, (svc) => [
      ...(svc || []),
      ...serviceShape,
    ]);
    markDirty(activePerson._id, true);
    setModal(false);
  };

  if (loading && !salesPersons.length) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const totalAssigned = (salesPersons || []).reduce(
    (acc, p) => acc + (Array.isArray(p?.service) ? p.service.length : 0),
    0,
  );

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Assign Sales Person Pincodes{" "}
                <span className="count-pill">{totalAssigned}</span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ minWidth: 260 }}>
                  <Input
                    placeholder="Search Sales Person..."
                    value={personSearch}
                    onChange={(e) => setPersonSearch(e.target.value)}
                  />
                </div>

                <div style={{ minWidth: 320 }}>
                  <Input
                    placeholder="Search State / District / City / Pincode (inside opened rows)"
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== ALL SALES PERSONS TABLE (hierarchy: Person -> State -> District -> Pins) ===== */}
      <Row>
        <Col sm="12">
          <Card className="p-card" style={{ padding: 12 }}>
            <div className="p-table-wrap">
              <table className="p-table">
                <thead>
                  <tr>
                    <th style={{ width: "24%" }}>Sales Person</th>
                    <th style={{ width: "20%" }}>State</th>
                    <th style={{ width: "20%" }}>District</th>
                    <th style={{ width: "20%" }}>City</th>
                    <th style={{ width: "10%" }}>Pincode</th>
                    <th style={{ width: "6%" }}>Remove</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredSalesPersons.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 16, color: "#6b7280" }}>
                        No sales persons found.
                      </td>
                    </tr>
                  ) : (
                    filteredSalesPersons.map((sp) => {
                      const pid = sp?._id;
                      const pOpen = openPersons.has(pid);
                      const svc = uniqByPincode(
                        Array.isArray(sp?.service) ? sp.service : [],
                      );
                      const svcForView = serviceFiltered(svc);
                      const grouped = groupService(svcForView);

                      const personCount = svc.length;
                      const isDirty = !!dirtyById[pid];

                      return (
                        <React.Fragment key={pid}>
                          {/* PERSON ROW */}
                          <tr className="p-row">
                            <td className="p-cell">
                              <span
                                className="p-link"
                                onClick={() => togglePersonRow(pid)}
                              >
                                {pOpen ? "− " : "+ "}
                                {getUserDisplayName(sp)}
                              </span>{" "}
                              <span className="tag" style={{ marginLeft: 8 }}>
                                Pins: <b>{personCount}</b>
                              </span>
                              {isDirty && (
                                <span
                                  className="tag"
                                  style={{
                                    marginLeft: 8,
                                    borderColor: "#f59e0b",
                                  }}
                                >
                                  <span style={{ color: "#f59e0b" }}>
                                    Unsaved
                                  </span>
                                </span>
                              )}
                            </td>

                            <td className="p-cell pill-muted">—</td>
                            <td className="p-cell pill-muted">—</td>
                            <td className="p-cell pill-muted">—</td>
                            <td className="p-cell pill-muted">—</td>

                            <td
                              className="p-cell"
                              style={{ textAlign: "right" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Button
                                  className="btn-mini"
                                  style={{
                                    backgroundColor: "rgb(8, 91, 245)",
                                    borderColor: "rgb(8, 91, 245)",
                                  }}
                                  onClick={() => openModalForPerson(sp)}
                                >
                                  + Add
                                </Button>

                                <Button
                                  className="btn-mini"
                                  color="primary"
                                  onClick={() => saveChanges(sp)}
                                  disabled={!isDirty}
                                >
                                  {loading ? "Saving..." : "Save"}
                                </Button>
                              </div>
                            </td>
                          </tr>

                          {!pOpen ? null : grouped.length === 0 ? (
                            <tr className="p-row">
                              <td
                                className="p-cell indent-1"
                                colSpan={6}
                                style={{ color: "#6b7280" }}
                              >
                                No services added yet.
                              </td>
                            </tr>
                          ) : (
                            grouped.map(({ state, districts }) => {
                              const sKey = `${pid}||${state}`;
                              const sOpen = openStates.has(sKey);

                              return (
                                <React.Fragment key={`state-${sKey}`}>
                                  {/* STATE ROW */}
                                  <tr className="p-row">
                                    <td className="p-cell indent-1 pill-muted">
                                      —
                                    </td>
                                    <td className="p-cell">
                                      <span
                                        className="p-link"
                                        onClick={() =>
                                          toggleStateRow(pid, state)
                                        }
                                      >
                                        {sOpen ? "− " : "+ "}
                                        {state || "-"}
                                      </span>
                                    </td>
                                    <td className="p-cell pill-muted">—</td>
                                    <td className="p-cell pill-muted">—</td>
                                    <td className="p-cell pill-muted">—</td>
                                    <td className="p-cell p-check-cell">
                                      <Input
                                        type="checkbox"
                                        onChange={() => removeState(pid, state)}
                                        title="Remove this State (all pincodes)"
                                      />
                                    </td>
                                  </tr>

                                  {!sOpen
                                    ? null
                                    : districts.map(({ district, items }) => {
                                        const dKey = `${pid}||${state}||${district}`;
                                        const dOpen = openDistricts.has(dKey);

                                        return (
                                          <React.Fragment key={`dist-${dKey}`}>
                                            {/* DISTRICT ROW */}
                                            <tr className="p-row">
                                              <td className="p-cell indent-1 pill-muted">
                                                —
                                              </td>
                                              <td className="p-cell indent-1 pill-muted">
                                                —
                                              </td>
                                              <td className="p-cell">
                                                <span
                                                  className="p-link"
                                                  onClick={() =>
                                                    toggleDistrictRow(
                                                      pid,
                                                      state,
                                                      district,
                                                    )
                                                  }
                                                >
                                                  {dOpen ? "− " : "+ "}
                                                  {district || "-"}
                                                </span>
                                              </td>
                                              <td className="p-cell pill-muted">
                                                —
                                              </td>
                                              <td className="p-cell pill-muted">
                                                —
                                              </td>
                                              <td className="p-cell p-check-cell">
                                                <Input
                                                  type="checkbox"
                                                  onChange={() =>
                                                    removeDistrict(
                                                      pid,
                                                      state,
                                                      district,
                                                    )
                                                  }
                                                  title="Remove this District (all pincodes)"
                                                />
                                              </td>
                                            </tr>

                                            {/* PIN ROWS */}
                                            {dOpen &&
                                              items.map((srv, i) => (
                                                <tr
                                                  className="p-row"
                                                  key={`${pid}-${srv?.pincode}-${i}`}
                                                >
                                                  <td className="p-cell indent-1 pill-muted">
                                                    —
                                                  </td>
                                                  <td className="p-cell indent-1 pill-muted">
                                                    —
                                                  </td>
                                                  <td className="p-cell indent-2 pill-muted">
                                                    —
                                                  </td>
                                                  <td className="p-cell">
                                                    {srv?.city || ""}
                                                  </td>
                                                  <td className="p-cell">
                                                    {srv?.pincode || ""}
                                                  </td>
                                                  <td className="p-cell p-check-cell">
                                                    <Input
                                                      type="checkbox"
                                                      onChange={() =>
                                                        removePincode(
                                                          pid,
                                                          srv?.pincode,
                                                        )
                                                      }
                                                      title="Remove this Pincode"
                                                    />
                                                  </td>
                                                </tr>
                                              ))}
                                          </React.Fragment>
                                        );
                                      })}
                                </React.Fragment>
                              );
                            })
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Pincode Modal (ONLY State dropdown + Search) ===== */}
      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>
              Select Pincodes{" "}
              <span className="pill-muted">
                {activePerson ? `for ${getUserDisplayName(activePerson)}` : ""}
              </span>
            </span>

            <Button
              color="primary"
              size="sm"
              onClick={addSelectedPincodes}
              disabled={!selectedPins.length || !activePerson}
            >
              Add Selected
            </Button>

            <div style={{ flex: "1 1 120px" }} />
          </div>
        </ModalHeader>

        <ModalBody>
          {/* Filters */}
          <div className="pin-fbar">
            <div className="pin-frow">
              <div className="pin-fi">
                <label>State</label>
                <Input
                  type="select"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                >
                  <option value="">-- Select State --</option>
                  {(stateOptions || []).map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </Input>
              </div>

              <div className="pin-fi" style={{ minWidth: 260 }}>
                <label>Search</label>
                <Input
                  placeholder="Search district / city / pincode"
                  value={pinSearch}
                  disabled={!selectedState}
                  onChange={(e) => setPinSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {pinLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                margin: "2rem 0",
              }}
            >
              <Spinner />
            </div>
          ) : !selectedState ? (
            <div style={{ padding: 16, color: "#6b7280" }}>
              Please select a state…
            </div>
          ) : groupedModal.length === 0 ? (
            <div style={{ padding: 16, color: "#6b7280" }}>No data found…</div>
          ) : (
            <div className="p-table-wrap">
              <table className="p-table">
                <thead>
                  <tr>
                    <th style={{ width: "34%" }}>District</th>
                    <th style={{ width: "34%" }}>City</th>
                    <th style={{ width: "20%" }}>Pincode</th>
                    <th style={{ width: "12%" }}>Select</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedModal.map(({ district, items }) => {
                    const dKey = String(district || "-");
                    const dOpen = openDistrictsModal.has(dKey);
                    const districtChecked =
                      items.length > 0 && items.every((r) => isPinSelected(r));

                    return (
                      <React.Fragment key={`m-dist-${dKey}`}>
                        <tr className="p-row">
                          <td className="p-cell">
                            <span
                              className="p-link"
                              onClick={() => toggleDistrictModalRow(district)}
                            >
                              {dOpen ? "− " : "+ "}
                              {district || "-"}
                            </span>
                          </td>
                          <td className="p-cell pill-muted">—</td>
                          <td className="p-cell pill-muted">—</td>
                          <td className="p-cell p-check-cell">
                            <Input
                              type="checkbox"
                              checked={districtChecked}
                              onChange={() => togglePinGroup(items)}
                              title="Select entire District"
                            />
                          </td>
                        </tr>

                        {dOpen &&
                          items.map((r, idx) => (
                            <tr
                              className="p-row"
                              key={`m-leaf-${pinKey(r)}-${idx}`}
                            >
                              <td className="p-cell pill-muted"></td>
                              <td className="p-cell">{r?.city || "-"}</td>
                              <td className="p-cell">{r?.pincode || "-"}</td>
                              <td className="p-cell p-check-cell">
                                <Input
                                  type="checkbox"
                                  checked={isPinSelected(r)}
                                  onChange={() => togglePin(r)}
                                />
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
